import { Typography } from '@mui/material';
import { DateTimePickerAdapter, AutocompleteAdapter } from 'components/enhanced';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import StyledDropzone from 'components/ui/file-uploader/StyledDropzone';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { StatusOption } from 'shared/types/general';

const CLOSURE_STATUS_OPTIONS: Pick<StatusOption, 'value' | 'display_name'>[] = [
  { value: 3, display_name: 'PHONE_RESOLVED' },
  { value: 4, display_name: 'TECHNICIAN_RESOLVED' },
  { value: 5, display_name: 'INTERVENTION_COMPLETED' },
  { value: 6, display_name: 'UNDETERMINED' },
  { value: 9, display_name: 'INTERVENTION_COMPLETED_MISSING_REPORT' }
];

const INTERVENTION_COMPLETED_STATUS = 5;

export function resolveCloseTicketStatusValue(rawStatus: unknown): number | null {
  if (typeof rawStatus === 'number') return rawStatus;
  if (rawStatus && typeof rawStatus === 'object' && 'value' in rawStatus) {
    const v = (rawStatus as { value: unknown }).value;
    return typeof v === 'number' ? v : v != null ? Number(v) : null;
  }
  return null;
}

interface CloseTicketReportSectionProps {
  technicalReportFilename?: string | null;
  reportFile: File | null;
  onReportFile: (file: File | null) => void;
}

function CloseTicketReportSection({
  technicalReportFilename,
  reportFile,
  onReportFile
}: CloseTicketReportSectionProps) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.tickets.close-modal';
  const { values } = useFormState({ subscription: { values: true } });
  const statusValue = resolveCloseTicketStatusValue(values?.status);

  if (statusValue !== INTERVENTION_COMPLETED_STATUS) {
    return null;
  }

  const hasExistingReport = Boolean(technicalReportFilename);

  if (hasExistingReport) {
    return (
      <div className="w-full p-2">
        <Typography variant="body2" color="text.secondary">
          {t(`${translationPrefix}.report-already-present`, {
            filename: technicalReportFilename
          })}
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full p-2">
      <Typography variant="body2" className="mb-2">
        {t(`${translationPrefix}.report-required`)}
      </Typography>
      <StyledDropzone
        onDrop={(files) => {
          onReportFile(files[0] ?? null);
        }}
      />
      {reportFile && (
        <Typography variant="caption" className="mt-2 block">
          {reportFile.name}
        </Typography>
      )}
    </div>
  );
}

export interface CloseTicketFormValues {
  resolved_at: string;
  status: unknown;
  technical_report_file?: File | null;
}

interface CloseTicketFormParams {
  ticketId: number;
  technicalReportFilename?: string | null;
  onSubmit: (values: CloseTicketFormValues) => void | Promise<void>;
}

function CloseTicketForm(params: CloseTicketFormParams) {
  const { t } = useTranslation();
  const { technicalReportFilename, onSubmit } = params;
  const [reportFile, setReportFile] = useState<File | null>(null);

  const statusOptions = useMemo<StatusOption[]>(
    () =>
      CLOSURE_STATUS_OPTIONS.map((item) => ({
        ...item,
        display_name: t(`pages.tickets.status_options.${item.display_name}`)
      })),
    [t]
  );

  const handleSubmit = (values: Record<string, unknown>) => {
    return onSubmit({
      ...(values as Omit<CloseTicketFormValues, 'technical_report_file'>),
      technical_report_file: reportFile
    });
  };

  return (
    <div>
      <EnhancedForm onSubmit={handleSubmit} formId="closeTicket" hideControls>
        <div className="flex flex-wrap">
          <div className="w-full md:w-1/2 p-2">
            <IdentityField
              name="resolved_at"
              component={DateTimePickerAdapter}
              label={t(`global.resolved_at`)}
              initialValue={dayjs().format('YYYY-MM-DD HH:mm')}
              type="text"
              required
            />
          </div>
          <div className="p-2 w-full md:w-1/2">
            <IdentityField
              name="status"
              label={t(`global.status`)}
              component={AutocompleteAdapter}
              options={statusOptions}
              type="text"
              optionLabel="display_name"
              optionValue="value"
              required
            />
          </div>
          <CloseTicketReportSection
            technicalReportFilename={technicalReportFilename}
            reportFile={reportFile}
            onReportFile={setReportFile}
          />
        </div>
      </EnhancedForm>
    </div>
  );
}

export default CloseTicketForm;
